import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode, useCallback } from "react";
import { createChannel } from "bidc";
import { executeHotkey } from "@repo/common/hotkeys";
import { useTheme } from "next-themes";
import { useRouter } from "next/router";
import { getTargetOrigin, isAllowedOrigin } from "@/utils/bidc-origins";
import { BIDC_MESSAGE_TYPES } from "@/constants/bidc-message-types";
import { BIDC_PAGE_NAMESPACES } from "@/constants/bidc-page-namespaces";
import type { EmbedCommand, EmbedSession, ParentUpdate } from "@/contexts/embed-session-types";
import { createShortcutCommandBridge, type ShortcutCommandBridge } from "@/contexts/shortcut-command-bridge";
import type { ReactToPhpShortcutCommand, ShortcutCommandResult } from "@/contexts/shortcut-command-types";
export type { EmbedCommand, ParentUpdate, EmbedSession } from "@/contexts/embed-session-types";
type BidcChannel = ReturnType<typeof createChannel>;
export type BidcContextType = {
  channel: BidcChannel | null;
  send: (message: any) => Promise<void>;
  receive: (handler: (message: any) => Promise<any>) => void;
  ns: string;
  isReady: boolean;
  history: any[] | null;
  availableHeight: number | null;
  /** tell parent (PHP) to show or hide overlays (bottom nav bar, search bar) */
  setOverlaysVisible: (visible: boolean) => Promise<void>;
  dispatch: (command: EmbedCommand) => Promise<void>;
  subscribeParentUpdates: (cb: (ev: ParentUpdate) => void) => () => void;
  sendShortcutCommand: (command: ReactToPhpShortcutCommand) => Promise<ShortcutCommandResult>;
};
const BidcContext = createContext<BidcContextType | null>(null);

// module-level store to persist history across component remounts
// keyed by namespace to support multiple instances
const historyStore = new Map<string, any[] | null>();
type Props = {
  children: ReactNode;
  ns: string;
};
type ReceiveHandlerContext = {
  channel: BidcChannel;
  actualNs: string;
  ns: string;
  receiveShortcutCommand: ShortcutCommandBridge["receiveFromPhp"];
  setAvailableHeight: (h: number | null) => void;
  setTheme: (theme: string) => void;
  updateHistory: (newHistory: any[] | null) => void;
};
function sendWithPostMessageFallback(channel: {
  send: (m: any) => Promise<unknown>;
} | null, message: object, options?: {
  timeoutMs?: number;
  alwaysPostMessage?: boolean;
}): Promise<void> {
  const postMessage = () => {
    if (typeof window !== "undefined" && window.parent !== window) {
      window.parent.postMessage(message, getTargetOrigin());
    }
  };
  if (!channel) {
    postMessage();
    return Promise.resolve();
  }
  const sendPromise = options?.timeoutMs !== undefined ? Promise.race([channel.send(message), new Promise<never>((_, rej) => setTimeout(() => rej(new Error("Send timeout")), options.timeoutMs))]) : channel.send(message);
  return sendPromise.then(() => {
    if (options?.alwaysPostMessage) postMessage();
  }).catch(err => {
    console.warn(`[BIDC] send failed, using postMessage fallback:`, err);
    postMessage();
    if (options?.alwaysPostMessage) postMessage();
  });
}
function createReceiveHandlers(ctx: ReceiveHandlerContext, notifyParentUpdates: (ev: ParentUpdate) => void): Record<string, (msg: any) => Promise<object>> {
  const {
    channel,
    actualNs,
    ns,
    receiveShortcutCommand,
    setAvailableHeight,
    setTheme,
    updateHistory
  } = ctx;
  return {
    [BIDC_MESSAGE_TYPES.HEIGHT_UPDATE]: async (msg: any) => {
      const cleanIframeId = actualNs.replace("ns:", "");
      const message = {
        type: BIDC_MESSAGE_TYPES.IFRAME_HEIGHT,
        iframeId: cleanIframeId,
        height: msg.height
      };
      await sendWithPostMessageFallback(channel, message);
      return {
        success: true
      };
    },
    [BIDC_MESSAGE_TYPES.SET_AVAILABLE_HEIGHT]: async (msg: any) => {
      setAvailableHeight(msg.height);
      return {
        success: true
      };
    },
    [BIDC_MESSAGE_TYPES.SET_THEME]: async (msg: any) => {
      setTheme(msg.theme);
      notifyParentUpdates({
        kind: "theme",
        mode: msg.theme
      });
      return {
        success: true
      };
    },
    [BIDC_MESSAGE_TYPES.DRAWER_MESSAGE]: async () => ({
      success: true
    }),
    [BIDC_MESSAGE_TYPES.SET_HISTORY]: async (msg: any) => {
      updateHistory(msg.history);
      return {
        success: true
      };
    },
    [BIDC_MESSAGE_TYPES.PING]: async () => ({
      type: "pong",
      timestamp: Date.now()
    }),
    [BIDC_MESSAGE_TYPES.HEALTH_CHECK]: async () => ({
      ok: true,
      ts: Date.now(),
      ns
    }),
    [BIDC_MESSAGE_TYPES.GET_AVAILABLE_HEIGHT]: async () => ({
      success: false,
      error: "iframe should not receive getAvailableHeight requests"
    }),
    [BIDC_MESSAGE_TYPES.GET_HEIGHT]: async () => ({
      success: false,
      error: "iframe does not send height to parent"
    }),
    [BIDC_MESSAGE_TYPES.REFRESH_SITE_MAIN_BANNERS]: async () => ({
      success: true
    }),
    [BIDC_MESSAGE_TYPES.SHORTCUT_COMMAND]: receiveShortcutCommand
  };
}
export function BidcProvider({
  children,
  ns
}: Props) {
  const [channel, setChannel] = useState<BidcChannel | null>(null);
  const [isReady, setIsReady] = useState(false);
  const {
    setTheme
  } = useTheme();
  const router = useRouter();
  const actualNs = typeof router.query.ns === "string" ? router.query.ns : ns;
  const channelRef = useRef(channel);
  const isReadyRef = useRef(isReady);
  channelRef.current = channel;
  isReadyRef.current = isReady;
  const shortcutCommandBridge = useMemo(() => createShortcutCommandBridge({
    namespace: actualNs,
    getTransport: () => {
      const currentChannel = channelRef.current;
      return currentChannel && isReadyRef.current ? currentChannel : null;
    },
    execute: executeHotkey
  }), [actualNs]);
  const parentUpdateListenersRef = useRef(new Set<(ev: ParentUpdate) => void>());
  const notifyParentUpdates = useCallback((ev: ParentUpdate) => {
    parentUpdateListenersRef.current.forEach(fn => {
      try {
        fn(ev);
      } catch (e) {
        console.error(e);
      }
    });
  }, []);
  const subscribeParentUpdates = useCallback((cb: (ev: ParentUpdate) => void) => {
    parentUpdateListenersRef.current.add(cb);
    return () => {
      parentUpdateListenersRef.current.delete(cb);
    };
  }, []);
  const [history, setHistoryState] = useState<any[] | null>(() => historyStore.get(actualNs) ?? null);
  const [availableHeight, setAvailableHeightState] = useState<number | null>(null);
  const setAvailableHeight = useCallback((h: number | null) => {
    setAvailableHeightState(h);
    if (h != null) {
      notifyParentUpdates({
        kind: "availableHeight",
        px: h
      });
    }
  }, [notifyParentUpdates]);
  const updateHistory = useCallback((newHistory: any[] | null) => {
    const current = historyStore.get(actualNs);
    if (Array.isArray(newHistory)) {
      historyStore.set(actualNs, newHistory);
      setHistoryState(newHistory);
      notifyParentUpdates({
        kind: "history",
        entries: newHistory
      });
    } else if (current === null || current === undefined) {
      historyStore.set(actualNs, null);
      setHistoryState(null);
      notifyParentUpdates({
        kind: "history",
        entries: []
      });
    }
  }, [actualNs, notifyParentUpdates]);
  useEffect(() => {
    if (typeof window === "undefined" || window.parent === window || !router.isReady) {
      return;
    }
    if (actualNs === BIDC_PAGE_NAMESPACES.DEFAULT) {
      return;
    }
    const ch = createChannel(actualNs);
    const {
      receive
    } = ch;
    const handlePostMessage = (event: MessageEvent) => {
      if (!isAllowedOrigin(event.origin)) {
        console.warn(`[${actualNs}] Rejected message from unauthorized origin: ${event.origin}`);
        return;
      }
      if (event.data?.type === BIDC_MESSAGE_TYPES.SET_THEME) {
        setTheme(event.data.theme);
        notifyParentUpdates({
          kind: "theme",
          mode: event.data.theme
        });
      } else if (event.data?.type === BIDC_MESSAGE_TYPES.SET_AVAILABLE_HEIGHT) {
        setAvailableHeight(event.data.height);
      } else if (event.data?.type === BIDC_MESSAGE_TYPES.SET_HISTORY) {
        updateHistory(event.data.history);
      }
    };
    window.addEventListener("message", handlePostMessage);
    const handlerContext: ReceiveHandlerContext = {
      channel: ch,
      actualNs,
      ns,
      receiveShortcutCommand: shortcutCommandBridge.receiveFromPhp,
      setAvailableHeight,
      setTheme,
      updateHistory
    };
    const handlers = createReceiveHandlers(handlerContext, notifyParentUpdates);
    receive(async (msg: any) => {
      notifyParentUpdates({
        kind: "ingress",
        raw: msg
      });
      const handler = msg?.type ? handlers[msg.type] : undefined;
      if (handler) {
        return handler(msg) as Promise<any>;
      }
      return {
        success: false,
        error: "unknown_message"
      };
    });
    setChannel(ch);
    setIsReady(true);
    const readyMessage = {
      type: BIDC_MESSAGE_TYPES.READY,
      ns: actualNs
    };
    const heightRequestMessage = {
      type: BIDC_MESSAGE_TYPES.GET_AVAILABLE_HEIGHT
    };
    sendWithPostMessageFallback(ch, readyMessage, {
      timeoutMs: 2000
    }).then(() => {
      if (actualNs !== BIDC_PAGE_NAMESPACES.MODAL) {
        return ch.send(heightRequestMessage);
      }
    }).catch(error => {
      console.warn(`[${actualNs}] BIDC ready message failed or timed out, using postMessage fallback:`, error);
      if (typeof window !== "undefined" && window.parent !== window) {
        window.parent.postMessage(readyMessage, getTargetOrigin());
        if (actualNs !== BIDC_PAGE_NAMESPACES.MODAL) {
          window.parent.postMessage(heightRequestMessage, getTargetOrigin());
        }
      }
    });
    return () => {
      window.removeEventListener("message", handlePostMessage);
      ch.cleanup();
      setChannel(null);
      setIsReady(false);
    };
  }, [actualNs, setTheme, ns, router.isReady, updateHistory, notifyParentUpdates, setAvailableHeight, shortcutCommandBridge]);
  const send = async (message: any) => {
    if (!channel || !isReady) {
      throw new Error("Channel not ready");
    }
    return channel.send(message);
  };
  const setOverlaysVisible = useCallback(async (visible: boolean) => {
    const message_0 = {
      type: BIDC_MESSAGE_TYPES.SET_OVERLAYS_VISIBLE,
      source: "copilot-crm" as const,
      visible
    };
    const ch_0 = channelRef.current;
    const ready = isReadyRef.current;
    const inIframe = typeof window !== "undefined" && window.parent !== window;
    const sendPostMessage = () => {
      if (inIframe) {
        window.parent.postMessage(message_0, getTargetOrigin());
      }
    };
    if (!ch_0 || !ready) {
      sendPostMessage();
      return;
    }
    await sendWithPostMessageFallback(ch_0, message_0, {
      timeoutMs: 2000,
      alwaysPostMessage: true
    });
  }, []);
  const receive_0 = useCallback((handler_0: (message: any) => Promise<any>) => {
    if (channel) {
      channel.receive(handler_0);
    }
  }, [channel]);
  const dispatchCustomMessage = useCallback(async (message_1: Record<string, unknown>) => {
    const ch_1 = channelRef.current;
    if (ch_1) {
      try {
        await ch_1.send(message_1);
        return;
      } catch {
        // fall through to postMessage
      }
    }
    if (typeof window !== "undefined" && window.parent !== window) {
      window.parent.postMessage(message_1, "*");
    }
  }, []);
  const dispatch = useCallback(async (command: EmbedCommand) => {
    switch (command.kind) {
      case "reportContentHeight":
        {
          const ch_6 = channelRef.current;
          if (!ch_6 || !isReadyRef.current) {
            return;
          }
          await ch_6.send({
            type: BIDC_MESSAGE_TYPES.HEIGHT_UPDATE,
            height: command.px
          });
          break;
        }
      case "setOverlaysVisible":
        await setOverlaysVisible(command.visible);
        break;
      case "navigateParent":
        {
          const ch_5 = channelRef.current;
          if (!ch_5 || !isReadyRef.current) {
            throw new Error("Channel not ready");
          }
          await ch_5.send({
            type: "navigateTo",
            url: command.url
          });
          break;
        }
      case "closeModal":
        {
          const ch_4 = channelRef.current;
          if (!ch_4 || !isReadyRef.current) {
            throw new Error("Channel not ready");
          }
          await ch_4.send({
            type: BIDC_MESSAGE_TYPES.CLOSE_MODAL
          });
          break;
        }
      case "refreshSiteMainBanners":
        {
          const ch_3 = channelRef.current;
          if (!ch_3 || !isReadyRef.current) {
            throw new Error("Channel not ready");
          }
          await ch_3.send({
            type: BIDC_MESSAGE_TYPES.REFRESH_SITE_MAIN_BANNERS
          });
          break;
        }
      case "reload":
        {
          const ch_2 = channelRef.current;
          if (!ch_2 || !isReadyRef.current) {
            throw new Error("Channel not ready");
          }
          await ch_2.send({
            type: BIDC_MESSAGE_TYPES.RELOAD
          });
          break;
        }
      case "clearCache":
        await dispatchCustomMessage({
          type: "clearCache"
        });
        break;
      case "custom":
        await dispatchCustomMessage(command.message);
        break;
      default:
        {
          const _exhaustive: never = command;
          void _exhaustive;
        }
    }
  }, [dispatchCustomMessage, setOverlaysVisible]);
  const sendShortcutCommand = shortcutCommandBridge.sendToPhp;
  useEffect(() => {
    const storeHistory = historyStore.get(actualNs);
    if (storeHistory !== history) {
      setHistoryState(storeHistory ?? null);
    }
  }, [actualNs, history]);
  const historyValue = history ?? historyStore.get(actualNs) ?? null;
  const contextValue: BidcContextType = {
    channel,
    send,
    receive: receive_0,
    isReady,
    history: historyValue,
    availableHeight,
    setOverlaysVisible,
    dispatch,
    subscribeParentUpdates,
    sendShortcutCommand,
    ns: actualNs
  };
  return <BidcContext.Provider value={contextValue}>{children}</BidcContext.Provider>;
}
export function useBidc() {
  const context = useContext(BidcContext);
  if (!context) {
    throw new Error("useBidc must be used within a BidcProvider");
  }
  return context;
}
export function useEmbedSession() {
  const $ = _c(8);
  const ctx = useBidc();
  let t0;
  if ($[0] !== ctx.availableHeight || $[1] !== ctx.dispatch || $[2] !== ctx.history || $[3] !== ctx.isReady || $[4] !== ctx.ns || $[5] !== ctx.sendShortcutCommand || $[6] !== ctx.subscribeParentUpdates) {
    t0 = {
      namespace: ctx.ns,
      isReady: ctx.isReady,
      availableHeight: ctx.availableHeight,
      history: ctx.history,
      dispatch: ctx.dispatch,
      subscribeParentUpdates: ctx.subscribeParentUpdates,
      sendShortcutCommand: ctx.sendShortcutCommand
    };
    $[0] = ctx.availableHeight;
    $[1] = ctx.dispatch;
    $[2] = ctx.history;
    $[3] = ctx.isReady;
    $[4] = ctx.ns;
    $[5] = ctx.sendShortcutCommand;
    $[6] = ctx.subscribeParentUpdates;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  return t0;
}